// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Modal, Platform, Pressable, StyleSheet, TextInput, View } from 'react-native';
import { useEffect, useMemo, useState } from 'react';

import { Text } from '@/components/Themed';
import { MIN_PASSWORD_LENGTH, validatePasswordForm } from '@/features/profile/passwordValidation';
import { useI18n } from '@/i18n';
import appColors, { alpha } from '@/theme/colors';

type Props = {
  visible: boolean;
  /** Show and require the current-password field (false for accounts without a password yet). */
  requireCurrent: boolean;
  title: string;
  pending: boolean;
  errorText?: string | null;
  onSubmit: (currentPassword: string | null, newPassword: string) => void;
  onClose: () => void;
};

export default function ChangePasswordModal({
  visible,
  requireCurrent,
  title,
  pending,
  errorText,
  onSubmit,
  onClose,
}: Props) {
  const { t } = useI18n();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');

  // Clear the fields every time the modal opens so stale secrets never linger.
  useEffect(() => {
    if (visible) {
      setCurrentPassword('');
      setNewPassword('');
      setRepeatPassword('');
    }
  }, [visible]);

  const validationError = useMemo(
    () => validatePasswordForm({ requireCurrent, currentPassword, newPassword, repeatPassword }),
    [requireCurrent, currentPassword, newPassword, repeatPassword]
  );
  const isValid = validationError === null;
  const showValidationHint =
    !isValid && validationError !== 'currentRequired' && newPassword.length > 0 && repeatPassword.length > 0;
  const validationHint =
    validationError === 'mismatch'
      ? t('profile.passwordMismatch')
      : t('profile.passwordTooShort', { min: MIN_PASSWORD_LENGTH });

  const handleSubmit = () => {
    if (!isValid || pending) return;
    onSubmit(requireCurrent ? currentPassword : null, newPassword);
  };

  const fields = (
    <>
      {requireCurrent ? (
        <View style={styles.field}>
          <Text style={styles.fieldLabel}>{t('profile.currentPasswordLabel')}</Text>
          <TextInput
            value={currentPassword}
            onChangeText={setCurrentPassword}
            style={styles.input}
            secureTextEntry
            autoCapitalize="none"
            autoComplete="current-password"
            placeholderTextColor={appColors.text.muted}
            accessibilityLabel={t('profile.currentPasswordLabel')}
          />
        </View>
      ) : null}
      <View style={styles.field}>
        <Text style={styles.fieldLabel}>{t('profile.newPasswordLabel')}</Text>
        <TextInput
          value={newPassword}
          onChangeText={setNewPassword}
          placeholder={t('profile.newPasswordPlaceholder', { min: MIN_PASSWORD_LENGTH })}
          style={styles.input}
          secureTextEntry
          autoCapitalize="none"
          autoComplete="new-password"
          placeholderTextColor={appColors.text.muted}
          accessibilityLabel={t('profile.newPasswordLabel')}
        />
      </View>
      <View style={styles.field}>
        <Text style={styles.fieldLabel}>{t('profile.repeatPasswordLabel')}</Text>
        <TextInput
          value={repeatPassword}
          onChangeText={setRepeatPassword}
          style={styles.input}
          secureTextEntry
          autoCapitalize="none"
          autoComplete="new-password"
          placeholderTextColor={appColors.text.muted}
          accessibilityLabel={t('profile.repeatPasswordLabel')}
          onSubmitEditing={handleSubmit}
        />
      </View>
      {showValidationHint ? <Text style={styles.errorText}>{validationHint}</Text> : null}
      {errorText ? <Text style={styles.errorText}>{errorText}</Text> : null}
      <Pressable
        style={[styles.saveButton, (!isValid || pending) && styles.saveButtonDisabled]}
        onPress={handleSubmit}
        disabled={!isValid || pending}
        accessibilityLabel={pending ? t('profile.passwordSaving') : t('profile.save')}
      >
        <Text style={[styles.saveButtonText, (!isValid || pending) && styles.saveButtonTextDisabled]}>
          {pending ? t('profile.passwordSaving') : t('profile.save')}
        </Text>
      </Pressable>
    </>
  );

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose}>
        <Pressable style={styles.card} onPress={() => null} testID="change-password-modal">
          <View style={styles.headerRow}>
            <Text style={styles.title} numberOfLines={1}>
              {title}
            </Text>
            <Pressable style={styles.closeButton} onPress={onClose} accessibilityLabel={t('profile.close')}>
              <Text style={styles.closeButtonText}>✕</Text>
            </Pressable>
          </View>
          {Platform.OS === 'web' ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                handleSubmit();
              }}
              style={styles.webForm as never}
            >
              {fields}
            </form>
          ) : (
            fields
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: alpha(appColors.neutral.black, 0.5),
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: appColors.background.panel,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    padding: 16,
    gap: 14,
  },
  webForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  title: {
    flex: 1,
    color: appColors.text.primary,
    fontSize: 18,
    fontWeight: '700',
  },
  closeButton: {
    width: 30,
    height: 30,
    borderRadius: 15,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButtonText: {
    color: appColors.text.muted,
    fontSize: 16,
    fontWeight: '700',
  },
  field: {
    gap: 6,
  },
  fieldLabel: {
    color: appColors.text.secondary,
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  input: {
    borderWidth: 1,
    borderColor: appColors.border.strong,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: appColors.surface.input,
    color: appColors.text.primary,
    fontSize: 16,
  },
  errorText: {
    color: appColors.status.errorTextMuted,
    fontSize: 13,
    fontWeight: '600',
  },
  saveButton: {
    backgroundColor: appColors.background.interactive,
    borderRadius: 12,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveButtonDisabled: {
    backgroundColor: appColors.border.strong,
  },
  saveButtonText: {
    color: appColors.text.inverse,
    fontSize: 14,
    fontWeight: '800',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  saveButtonTextDisabled: {
    color: appColors.text.muted,
  },
});
